import type React from "react";
import { useImperativeHandle, useRef } from "react";
import TagManager from "react-gtm-module";
import { useLocation } from "react-router-dom";
import type { GaFormEventTypeModel, GaFormModel } from "../types";
import { useFormStart } from "./useFormStart";
import { useFormSubmit } from "./useFormSubmit";
import { useFormVisibility } from "./useFormVisibility";

export interface GaFormHandle {
	requestSubmit: () => void;
	submitGa: () => void;
}

export interface GaFormProps extends GaFormModel {
	children: React.ReactNode;
	onSubmit?: (e?: React.FormEvent<HTMLFormElement>) => void;
	getFormContent?: () => string | null;
	getFormContent2?: () => string | null;
	getFormAdditionalContent?: () => string | null;
	deferGaSubmit?: boolean;
	ref?: React.Ref<GaFormHandle>;
}

export function GaForm({ ref, ...props }: GaFormProps) {
	const location = useLocation();
	const formRef = useRef<HTMLFormElement>(null);

	const pushToDataLayer = (model: GaFormEventTypeModel) => {
		if (!model || !model.event) {
			return;
		}

		TagManager.dataLayer({
			dataLayer: {
				...model,
				pagePath: location.pathname,
			},
		});
	};

	useFormVisibility({ ...props, pushToDataLayer });
	useFormStart({ ...props, formRef, pushToDataLayer });
	const { handleSubmit, submitGa } = useFormSubmit({
		...props,
		formRef,
		pushToDataLayer,
	});

	useImperativeHandle(ref, () => ({
		requestSubmit: () => {
			formRef.current?.requestSubmit();
		},
		submitGa: () => {
			submitGa();
		},
	}));

	return (
		<form ref={formRef} onSubmit={handleSubmit}>
			{props.children}
		</form>
	);
}
